// Firebase Analytics (GA4) com gate de consentimento + área pública.
//
// Só carrega/coleta quando as duas condições valem ao mesmo tempo:
// - consentimento de analytics dado no CookieConsentBanner
//   (initAnalyticsIfConsented / disableAnalytics);
// - usuário dentro da área pública (PublicLayout chama setPublicAreaActive).
// Painel admin/organizador e /login ficam de fora: o SDK nem carrega se a
// pessoa entra direto por lá, e se ela navega da área pública pra lá (SPA),
// a coleta é desligada (setAnalyticsCollectionEnabled).
//
// page_view é manual (send_page_view: false), disparado pelo SeoHead de
// cada página pública — é ele que sabe o título final da página.

let consentGiven = false;
let publicAreaActive = false;
let analyticsPromise = null; // Promise<Analytics | null>, carregada uma vez só
let lastPageView = null; // { params, sent } — o page_view da página atual

const isActive = () => consentGiven && publicAreaActive;

async function loadAnalytics() {
  if (!analyticsPromise) {
    analyticsPromise = (async () => {
      try {
        const [{ initializeAnalytics, isSupported }, { getFirebase }] = await Promise.all([
          import('firebase/analytics'),
          import('./firebase'),
        ]);
        if (!(await isSupported())) return null;
        return initializeAnalytics(getFirebase(), { config: { send_page_view: false } });
      } catch (e) {
        console.warn('Analytics indisponível:', e);
        return null;
      }
    })();
  }
  return analyticsPromise;
}

// Liga/desliga a coleta conforme o estado atual. Carrega o SDK na primeira
// vez que as duas condições valem e manda o page_view pendente da página
// em que a pessoa está (ex.: aceitou os cookies depois de a página abrir).
async function syncAnalyticsState() {
  if (!isActive() && !analyticsPromise) return; // nunca carregou, segue sem carregar
  const analytics = await loadAnalytics();
  if (!analytics) return;

  const { setAnalyticsCollectionEnabled, logEvent } = await import('firebase/analytics');
  setAnalyticsCollectionEnabled(analytics, isActive());

  if (isActive() && lastPageView && !lastPageView.sent) {
    lastPageView.sent = true;
    logEvent(analytics, 'page_view', lastPageView.params);
  }
}

// Remove os cookies do GA (_ga, _ga_<id>) ao recusar/revogar — no domínio
// atual e nos domínios-pai (o gtag grava no domínio raiz, ex.: .joynapp.com.br).
function clearGaCookies() {
  try {
    const names = document.cookie
      .split(';')
      .map((c) => c.split('=')[0].trim())
      .filter((name) => name === '_ga' || name.startsWith('_ga_'));
    const parts = window.location.hostname.split('.');
    const domains = [''];
    for (let i = 0; i < parts.length - 1; i++) domains.push(`; domain=.${parts.slice(i).join('.')}`);
    names.forEach((name) => {
      domains.forEach((domain) => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`;
      });
    });
  } catch {
    // sem acesso a cookies — nada a limpar
  }
}

// Chamado pelo CookieConsentBanner ao abrir (decisão salva) e ao aceitar.
export async function initAnalyticsIfConsented(analyticsConsentGiven) {
  consentGiven = Boolean(analyticsConsentGiven);
  if (!consentGiven) return;
  await syncAnalyticsState();
}

// Chamado quando o usuário recusa ou revoga o consentimento de analytics.
// O SDK não "descarrega" depois de carregado, então a coleta é desligada
// (setAnalyticsCollectionEnabled(false)) e os cookies do GA são apagados.
export function disableAnalytics() {
  consentGiven = false;
  syncAnalyticsState();
  clearGaCookies();
}

// PublicLayout: true ao montar, false ao desmontar (saiu da área pública).
export function setPublicAreaActive(active) {
  publicAreaActive = Boolean(active);
  if (!active) lastPageView = null;
  syncAnalyticsState();
}

// SeoHead de cada página pública, quando o título final está pronto.
export function trackPageView(path, title) {
  lastPageView = {
    params: {
      page_path: path,
      page_title: title,
      page_location: window.location.href,
    },
    sent: false,
  };
  syncAnalyticsState();
}

// Evento qualquer (view_item, click_download_app...). Sem consentimento ou
// fora da área pública, não faz nada.
export async function trackEvent(name, params) {
  if (!isActive()) return;
  const analytics = await loadAnalytics();
  if (!analytics || !isActive()) return;
  const { logEvent } = await import('firebase/analytics');
  logEvent(analytics, name, params);
}
